<template>
  <v-container class="ps-admin">
    <div class="page-header">
      <h2>IABF {{ adminFestivalYear }} — Photoshoots</h2>
    </div>
    <p class="muted">
      $85 for 15 minutes and 2 edited photos. Returning-dancer photoshoot gifts cover a slot.
      Indoor: {{ config.indoorLocation }}. Outdoor: {{ config.outdoorLocation }}.
    </p>

    <div class="ps-tabs">
      <button type="button" class="ps-tab" :class="{ on: tab === 'calendar' }" @click="tab = 'calendar'">Calendar</button>
      <button type="button" class="ps-tab" :class="{ on: tab === 'proofs' }" @click="tab = 'proofs'">Proofs</button>
      <button type="button" class="ps-tab" :class="{ on: tab === 'queue' }" @click="tab = 'queue'">Editing queue</button>
    </div>

    <template v-if="tab === 'calendar'">
      <section class="ps-card">
        <h3>Locations</h3>
        <div class="ps-row">
          <label>Indoor
            <input v-model="config.indoorLocation" class="ps-input" @blur="saveConfig">
          </label>
          <label>Outdoor
            <input v-model="config.outdoorLocation" class="ps-input" @blur="saveConfig">
          </label>
          <label>Default director
            <input v-model="config.defaultDirectorName" class="ps-input" list="ps-directors" @blur="saveConfig">
          </label>
          <label>Default editor
            <select class="ps-input" :value="config.defaultEditorId" @change="onDefaultEditor($event)">
              <option value="">— assign later —</option>
              <option v-for="f in facultyEditors" :key="f.id" :value="f.id">{{ f.name }}</option>
            </select>
          </label>
          <label>Cancel until (optional)
            <input v-model="config.cancelUntilIso" type="date" class="ps-input" @blur="saveConfig">
          </label>
        </div>
        <p class="hint">Changing indoor/outdoor names updates the labels dancers see. Existing slots keep their indoor/outdoor type.</p>
        <label class="ps-check">
          <input type="checkbox" :checked="!!config.dancerBookingOpen" @change="onBookingOpen($event)">
          Dancers can book
        </label>
        <p class="hint">Off: only julie@brocheballet.com sees the dancer schedule. On: every dancer can book.</p>
      </section>

      <section class="ps-card">
        <h3>Add a block of time</h3>
        <div class="ps-row">
          <label>Date
            <input v-model="block.date" type="date" class="ps-input">
          </label>
          <label>Start
            <input v-model="block.start" type="time" class="ps-input">
          </label>
          <label>End
            <input v-model="block.end" type="time" class="ps-input">
          </label>
          <label>Setting
            <select v-model="block.setting" class="ps-input">
              <option value="indoor">Indoor</option>
              <option value="outdoor">Outdoor</option>
            </select>
          </label>
          <label>Director
            <input v-model="block.directorName" class="ps-input" list="ps-directors" placeholder="Julie Gill">
          </label>
        </div>
        <datalist id="ps-directors">
          <option v-for="f in facultyNames" :key="f" :value="f" />
        </datalist>
        <p class="hint">{{ previewCount }} fifteen-minute slot{{ previewCount === 1 ? '' : 's' }} in this block.</p>
        <v-btn small color="primary" :disabled="!previewCount || adding" :loading="adding" @click="addBlock">
          Add {{ previewCount }} slot{{ previewCount === 1 ? '' : 's' }}
        </v-btn>
        <span v-if="blockNote" class="flash">{{ blockNote }}</span>
      </section>

      <section class="ps-card">
        <h3>Calendar</h3>
        <p v-if="loading" class="muted">Loading…</p>
        <p v-else-if="!slots.length" class="muted">No slots yet — add a block above.</p>
        <div v-for="day in days" :key="day.date" class="ps-day">
          <h4>{{ day.label }}</h4>
          <table class="ps-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Where</th>
                <th>Director</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in day.slots" :key="s.id" :class="{ 'is-break': s.kind === 'break', 'is-booked': s.status === 'booked' }">
                <td class="mono">{{ formatRange(s) }}</td>
                <td>{{ settingLabel(s.setting) }} · {{ locationFor(s.setting) }}</td>
                <td>
                  <input
                    class="ps-input ps-input-inline"
                    :value="s.directorName"
                    @blur="onDirectorBlur(s, $event)"
                  >
                </td>
                <td>
                  <span v-if="s.kind === 'break'">Break</span>
                  <span v-else-if="s.status === 'booked'">Booked{{ s.bookedDancerName ? ' — ' + s.bookedDancerName : '' }}</span>
                  <span v-else-if="s.status === 'held'">Held</span>
                  <span v-else>Open</span>
                </td>
                <td class="ps-actions">
                  <button type="button" class="linkish" @click="toggleBreak(s)">
                    {{ s.kind === 'break' ? 'Make photoshoot' : 'Make break' }}
                  </button>
                  <button
                    v-if="s.status !== 'booked'"
                    type="button"
                    class="linkish danger"
                    @click="removeSlot(s)"
                  >Remove</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </template>

    <template v-if="tab === 'proofs'">
      <section v-if="julieChooseList.length" class="ps-card">
        <h3>Julie, please choose</h3>
        <p class="muted">
          These dancers paid $30 for you to pick their included edited photos.
          Choose the keepers, then send them to the editing queue.
        </p>
        <article v-for="b in julieChooseList" :key="'jc-' + b.id" class="julie-block">
          <div class="ps-booking-head">
            <h4>{{ b.dancerName || 'Dancer' }}</h4>
            <span class="muted">Pick {{ includedFor(b) }} · {{ juliePickCount(b) }} chosen</span>
          </div>
          <p class="muted">{{ slotSummary(b) }}</p>
          <div class="ps-thumbs">
            <div
              v-for="p in photosFor(b.id)"
              :key="p.id"
              class="ps-thumb"
              :class="{ hid: p.hidden, picked: isJuliePick(p.id) }"
            >
              <img :src="p.downloadUrl" :alt="p.fileName">
              <span class="ps-thumb-name">
                {{ p.fileName }}
                <em v-if="p.rating"> · {{ p.rating }}</em>
              </span>
              <button type="button" class="thumb-btn" @click="toggleHide(p)">
                {{ p.hidden ? 'Unhide' : 'Hide' }}
              </button>
              <button
                v-if="!p.hidden"
                type="button"
                class="thumb-btn"
                @click="toggleJuliePick(p.id)"
              >{{ isJuliePick(p.id) ? 'Chosen' : 'Choose' }}</button>
            </div>
          </div>
          <v-btn
            small
            color="primary"
            :disabled="!juliePickCount(b) || julieSubmitting === b.id"
            :loading="julieSubmitting === b.id"
            @click="submitJuliePicks(b)"
          >Send {{ juliePickCount(b) }} to editing</v-btn>
        </article>
      </section>

      <section class="ps-card">
        <h3>Remind dancers</h3>
        <p class="muted">
          Copies emails for dancers who have proofs waiting but have not submitted their picks yet.
        </p>
        <v-btn small outlined :disabled="!unselectedEmails.length" @click="copyUnselectedEmails">
          Copy un-selected dancer emails
        </v-btn>
        <span class="muted count-note">{{ unselectedEmails.length }} waiting</span>
        <span v-if="emailNote" class="flash">{{ emailNote }}</span>
      </section>

      <section class="ps-card">
        <h3>Inbox — unassigned proofs</h3>
        <p class="hint">Upload a dump here. We’ll guess the dancer from the file time vs. their appointment, then you can Hide duds or move leftovers.</p>
        <input type="file" accept="image/*" multiple @change="onInboxFiles">
        <p v-if="uploadNote" class="flash">{{ uploadNote }}</p>
        <div v-if="inboxPhotos.length" class="ps-thumbs">
          <label v-for="p in inboxPhotos" :key="p.id" class="ps-thumb" :class="{ hid: p.hidden, picked: selectedInbox[p.id] }">
            <input type="checkbox" :checked="!!selectedInbox[p.id]" @change="toggleInbox(p.id, $event)">
            <img :src="p.downloadUrl" :alt="p.fileName">
            <span class="ps-thumb-name">{{ p.fileName }}</span>
            <button type="button" class="thumb-btn" @click.prevent="toggleHide(p)">
              {{ p.hidden ? 'Unhide' : 'Hide' }}
            </button>
          </label>
        </div>
        <div v-if="inboxPhotos.length" class="ps-row" style="margin-top:12px">
          <label>Move checked to
            <select v-model="moveTargetId" class="ps-input">
              <option value="">— pick a dancer —</option>
              <option v-for="b in bookedList" :key="b.id" :value="b.id">{{ b.dancerName }}</option>
            </select>
          </label>
          <v-btn small color="primary" :disabled="!moveTargetId || !inboxCheckedIds.length" @click="moveInbox">
            Move {{ inboxCheckedIds.length }}
          </v-btn>
        </div>
      </section>

      <section v-for="b in bookedList" :key="b.id" class="ps-card">
        <div class="ps-booking-head">
          <h3>{{ b.dancerName || 'Dancer' }}</h3>
          <span class="muted">{{ bookingStatus(b) }}</span>
        </div>
        <p class="muted">{{ slotSummary(b) }}</p>
        <div class="ps-row">
          <label>Editor
            <select class="ps-input" :value="b.editorId || ''" @change="onEditorChange(b, $event)">
              <option value="">— none —</option>
              <option v-for="f in facultyEditors" :key="f.id" :value="f.id">{{ f.name }}</option>
            </select>
          </label>
          <label>Add proofs
            <input type="file" accept="image/*" multiple @change="onBookingFiles(b, $event)">
          </label>
        </div>
        <div class="ps-thumbs">
          <div v-for="p in photosFor(b.id)" :key="p.id" class="ps-thumb" :class="{ hid: p.hidden, picked: p.selected }">
            <img :src="p.downloadUrl" :alt="p.fileName">
            <span class="ps-thumb-name">{{ p.fileName }}</span>
            <button type="button" class="thumb-btn" @click="toggleHide(p)">
              {{ p.hidden ? 'Unhide' : 'Hide' }}
            </button>
          </div>
        </div>
        <p v-if="!photosFor(b.id).length" class="hint">No proofs yet.</p>
      </section>
      <p v-if="!bookedList.length" class="muted">No bookings yet.</p>
    </template>

    <template v-if="tab === 'queue'">
      <section class="ps-card">
        <h3>Editing queue</h3>
        <p class="muted">Dancers who have submitted picks, in the order they sent them.</p>
        <div v-for="(b, i) in queueList" :key="b.id" class="queue-row">
          <strong>#{{ i + 1 }}</strong>
          <span>{{ b.dancerName }}</span>
          <span class="muted">{{ (b.selectedPhotoIds || []).length }} photo{{ (b.selectedPhotoIds || []).length === 1 ? '' : 's' }}</span>
          <span class="muted">{{ b.editorName || 'No editor' }}</span>
          <span v-if="b.editedReady" class="flash">Ready</span>
          <button v-else type="button" class="linkish" @click="markReady(b)">Mark edited ready</button>
        </div>
        <p v-if="!queueList.length" class="muted">Nobody in the queue yet.</p>
      </section>

      <section v-for="b in queueList" :key="'q-' + b.id" class="ps-card">
        <h3>{{ b.dancerName }} — selected for editing</h3>
        <div class="ps-thumbs">
          <div v-for="p in selectedPhotosFor(b)" :key="p.id" class="ps-thumb">
            <img :src="p.editedUrl || p.downloadUrl" :alt="p.fileName">
            <span class="ps-thumb-name">{{ p.fileName }}</span>
            <label class="thumb-btn">
              Upload edited
              <input type="file" accept="image/*" hidden @change="onEditedFile(p, b, $event)">
            </label>
            <span v-if="p.editedUrl" class="flash">Edited</span>
          </div>
        </div>
      </section>
    </template>
  </v-container>
</template>

<script>
import { functions, storage } from '@/firebase'
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import {
  formatSlotRange,
  locationForSetting,
  splitBlockIntoSlots
} from '@/config/iabf-photoshoot'
import { festivalWeek } from '@/config/iabf-festival-dates'

export default {
  name: 'PhotoshootAdmin',
  mixins: [iabfAdminMixin],
  data () {
    return {
      tab: 'calendar',
      loading: true,
      adding: false,
      blockNote: '',
      emailNote: '',
      uploadNote: '',
      config: {
        indoorLocation: 'Sculpture room',
        outdoorLocation: 'Urban amphitheater',
        defaultDirectorName: 'Julie Gill',
        defaultDirectorId: '',
        defaultEditorName: '',
        defaultEditorId: '',
        defaultEditorUid: '',
        defaultEditorEmail: '',
        cancelUntilIso: '',
        dancerBookingOpen: false
      },
      block: {
        date: '',
        start: '09:00',
        end: '12:00',
        setting: 'indoor',
        directorName: 'Julie Gill'
      },
      slots: [],
      faculty: [],
      bookings: [],
      photos: [],
      selectedInbox: {},
      moveTargetId: '',
      juliePicks: {},
      julieSubmitting: ''
    }
  },
  computed: {
    facultyNames () {
      const names = (this.faculty || []).map((f) => {
        return [f.firstName, f.lastName].filter(Boolean).join(' ').trim()
      }).filter(Boolean)
      if (names.indexOf('Julie Gill') === -1) {
        names.unshift('Julie Gill')
      }
      return names
    },
    facultyEditors () {
      return (this.faculty || []).map((f) => ({
        id: f.id,
        name: [f.firstName, f.lastName].filter(Boolean).join(' ').trim() || f.email,
        uid: f.uid || '',
        email: String(f.email || '').toLowerCase()
      })).filter((f) => f.name)
    },
    previewCount () {
      return splitBlockIntoSlots(this.block.start, this.block.end).length
    },
    days () {
      const byDate = {}
      ;(this.slots || []).forEach((s) => {
        const d = s.date || ''
        if (!byDate[d]) {
          byDate[d] = []
        }
        byDate[d].push(s)
      })
      return Object.keys(byDate).sort().map((date) => ({
        date,
        label: this.dayLabel(date),
        slots: byDate[date]
      }))
    },
    bookedList () {
      return (this.bookings || []).filter((b) => b.status === 'booked')
        .sort((a, b) => String(a.dancerName || '').localeCompare(String(b.dancerName || '')))
    },
    inboxPhotos () {
      return (this.photos || []).filter((p) => p.kind !== 'edited' && !p.bookingId)
    },
    inboxCheckedIds () {
      return Object.keys(this.selectedInbox).filter((id) => this.selectedInbox[id])
    },
    julieChooseList () {
      return (this.bookings || []).filter((b) => b.status === 'booked' && b.julieChoose && !b.proofsSubmitted)
        .sort((a, b) => (Number(a.julieChooseAt) || 0) - (Number(b.julieChooseAt) || 0))
    },
    unselectedEmails () {
      const emails = []
      const seen = {}
      ;(this.bookings || []).forEach((b) => {
        if (b.status !== 'booked' || b.proofsSubmitted || !b.proofsReady || b.julieChoose) {
          return
        }
        const email = String(b.dancerEmail || '').trim().toLowerCase()
        if (!email || seen[email]) {
          return
        }
        seen[email] = true
        emails.push(email)
      })
      return emails
    },
    queueList () {
      return (this.bookings || []).filter((b) => b.status === 'booked' && b.proofsSubmitted)
        .sort((a, b) => (Number(a.proofsSubmittedAt) || 0) - (Number(b.proofsSubmittedAt) || 0))
    }
  },
  watch: {
    adminFestivalYear () {
      this.reload()
    }
  },
  created () {
    const week = festivalWeek(this.adminFestivalYear)
    this.block.date = week.arrivalIso || ''
    this.reload()
  },
  methods: {
    locationFor (setting) {
      return locationForSetting(this.config, setting)
    },
    settingLabel (setting) {
      return setting === 'outdoor' ? 'Outdoor' : 'Indoor'
    },
    formatRange (s) {
      return formatSlotRange(s.startMin, s.endMin)
    },
    dayLabel (iso) {
      const [y, m, d] = String(iso || '').split('-').map(Number)
      if (!y || !m || !d) {
        return iso
      }
      return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      })
    },
    photosFor (bookingId) {
      return (this.photos || []).filter((p) => p.bookingId === bookingId && p.kind !== 'edited')
    },
    includedFor (booking) {
      const slots = Array.isArray(booking.slotIds) ? booking.slotIds.length : 0
      return Math.max(2, slots * 2)
    },
    isJuliePick (id) {
      return !!this.juliePicks[id]
    },
    juliePickCount (booking) {
      return this.photosFor(booking.id).filter((p) => this.juliePicks[p.id]).length
    },
    toggleJuliePick (id) {
      this.$set(this.juliePicks, id, !this.juliePicks[id])
    },
    async submitJuliePicks (booking) {
      const ids = this.photosFor(booking.id).filter((p) => this.juliePicks[p.id]).map((p) => p.id)
      if (!ids.length) {
        return
      }
      this.julieSubmitting = booking.id
      try {
        const callable = functions.httpsCallable('submitPhotoshootJuliePicks')
        await callable({ year: this.adminFestivalYear, bookingId: booking.id, photoIds: ids })
        booking.proofsSubmitted = true
        booking.selectedPhotoIds = ids
        ids.forEach((id) => {
          const p = this.photos.find((x) => x.id === id)
          if (p) {
            p.selected = true
            p.rating = 'love'
          }
        })
      } catch (e) {
        window.alert((e && e.message) || 'Could not send those picks')
      }
      this.julieSubmitting = ''
    },
    selectedPhotosFor (booking) {
      const ids = booking.selectedPhotoIds || []
      const byId = {}
      this.photos.forEach((p) => { byId[p.id] = p })
      return ids.map((id) => byId[id]).filter(Boolean)
    },
    bookingStatus (b) {
      if (b.editedReady) {
        return 'Edited photos ready'
      }
      if (b.proofsSubmitted) {
        return 'Picks submitted'
      }
      if (b.julieChoose) {
        return 'Julie choosing'
      }
      if (b.proofsReady) {
        return (b.proofCount || 0) + ' proofs — waiting on dancer'
      }
      return 'No proofs yet'
    },
    slotSummary (b) {
      const byId = {}
      this.slots.forEach((s) => { byId[s.id] = s })
      return (b.slotIds || []).map((id) => {
        const s = byId[id]
        if (!s) {
          return id
        }
        return this.dayLabel(s.date) + ' ' + this.formatRange(s) + ' · ' + this.settingLabel(s.setting)
      }).join(' · ')
    },
    editorFromId (id) {
      return this.facultyEditors.find((f) => f.id === id) || null
    },
    async reload () {
      this.loading = true
      try {
        const [config, slots, faculty, bookings, photos] = await Promise.all([
          this.$store.dispatch('loadPhotoshootConfig', { useAdminYear: true }),
          this.$store.dispatch('loadPhotoshootSlots', { useAdminYear: true }),
          this.$store.dispatch('loadIabfFaculty'),
          this.$store.dispatch('loadPhotoshootBookings', { useAdminYear: true }),
          this.$store.dispatch('loadPhotoshootPhotos', { useAdminYear: true })
        ])
        this.config = Object.assign({}, this.config, config || {})
        this.slots = slots || []
        this.faculty = faculty || []
        this.bookings = bookings || []
        this.photos = photos || []
        if (!this.block.directorName) {
          this.block.directorName = this.config.defaultDirectorName || 'Julie Gill'
        }
      } catch (e) {
        console.warn('photoshoot admin load failed', e)
      }
      this.loading = false
    },
    async saveConfig () {
      try {
        const saved = await this.$store.dispatch('savePhotoshootConfig', this.config)
        this.config = Object.assign({}, this.config, saved)
      } catch (e) {
        console.warn('save photoshoot config failed', e)
      }
    },
    async onBookingOpen (ev) {
      this.config.dancerBookingOpen = !!(ev && ev.target && ev.target.checked)
      await this.saveConfig()
    },
    async onDefaultEditor (ev) {
      const id = ev && ev.target ? ev.target.value : ''
      const f = this.editorFromId(id)
      this.config.defaultEditorId = f ? f.id : ''
      this.config.defaultEditorName = f ? f.name : ''
      this.config.defaultEditorUid = f ? f.uid : ''
      this.config.defaultEditorEmail = f ? f.email : ''
      await this.saveConfig()
    },
    async addBlock () {
      this.adding = true
      this.blockNote = ''
      try {
        const result = await this.$store.dispatch('addPhotoshootBlock', {
          date: this.block.date,
          start: this.block.start,
          end: this.block.end,
          setting: this.block.setting,
          directorName: this.block.directorName || this.config.defaultDirectorName
        })
        const skipped = result && result.skipped ? result.skipped : 0
        this.blockNote = 'Added ' + (result.added || 0) + ' slot' + ((result.added === 1) ? '' : 's') +
          (skipped ? ' (skipped ' + skipped + ' already on the calendar)' : '')
        await this.reload()
      } catch (e) {
        this.blockNote = (e && e.message) || 'Could not add block'
      }
      this.adding = false
    },
    async toggleBreak (slot) {
      const next = slot.kind === 'break' ? 'shoot' : 'break'
      await this.$store.dispatch('updatePhotoshootSlot', { id: slot.id, fields: { kind: next } })
      slot.kind = next
    },
    async onDirectorBlur (slot, ev) {
      const name = String(ev && ev.target ? ev.target.value : '').trim()
      if (name === slot.directorName) {
        return
      }
      await this.$store.dispatch('updatePhotoshootSlot', { id: slot.id, fields: { directorName: name } })
      slot.directorName = name
    },
    async removeSlot (slot) {
      if (slot.status === 'booked') {
        return
      }
      await this.$store.dispatch('deletePhotoshootSlot', slot.id)
      this.slots = this.slots.filter((s) => s.id !== slot.id)
    },
    async copyUnselectedEmails () {
      const emails = this.unselectedEmails
      if (!emails.length) {
        return
      }
      const text = emails.join('\n')
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text)
        }
        this.emailNote = 'Copied ' + emails.length + ' email' + (emails.length === 1 ? '' : 's')
      } catch (e) {
        window.prompt('Copy these emails:', text)
        this.emailNote = 'Copy from the prompt'
      }
      setTimeout(() => { this.emailNote = '' }, 2400)
    },
    toggleInbox (id, ev) {
      const on = !!(ev && ev.target && ev.target.checked)
      this.$set(this.selectedInbox, id, on)
    },
    guessBookingForTime (ms) {
      const matches = []
      const byId = {}
      this.slots.forEach((s) => { byId[s.id] = s })
      this.bookedList.forEach((b) => {
        (b.slotIds || []).forEach((id) => {
          const s = byId[id]
          if (!s) {
            return
          }
          const start = this.slotMs(s)
          const end = start + 15 * 60 * 1000
          const pad = 20 * 60 * 1000
          if (ms >= start - pad && ms <= end + pad) {
            matches.push(b)
          }
        })
      })
      const unique = []
      const seen = {}
      matches.forEach((b) => {
        if (!seen[b.id]) {
          seen[b.id] = true
          unique.push(b)
        }
      })
      return unique.length === 1 ? unique[0] : null
    },
    slotMs (s) {
      const hh = String(Math.floor((s.startMin || 0) / 60)).padStart(2, '0')
      const mm = String((s.startMin || 0) % 60).padStart(2, '0')
      return new Date(s.date + 'T' + hh + ':' + mm + ':00-04:00').getTime()
    },
    async uploadFiles (files, booking) {
      const list = Array.from(files || []).filter((f) => f && f.type && f.type.indexOf('image/') === 0)
      if (!list.length) {
        return
      }
      const cols = this.$store.getters.adminIabf
      const prefix = (cols && cols.storage && cols.storage.photoshoot) || ('iabf-photoshoot-' + this.adminFestivalYear)
      const rows = []
      this.uploadNote = 'Uploading ' + list.length + '…'
      for (let i = 0; i < list.length; i++) {
        const file = list[i]
        const safe = String(file.name || 'photo.jpg').replace(/[^\w.\-]+/g, '_')
        const guessed = booking || this.guessBookingForTime(file.lastModified || Date.now())
        const folder = guessed ? guessed.id : 'inbox'
        const path = prefix + '/proofs/' + folder + '/' + Date.now() + '-' + i + '-' + safe
        const snap = await storage.ref(path).put(file, { contentType: file.type || 'image/jpeg' })
        const url = await snap.ref.getDownloadURL()
        const editor = guessed && guessed.editorId
          ? {
            editorId: guessed.editorId,
            editorName: guessed.editorName,
            editorUid: guessed.editorUid,
            editorEmail: guessed.editorEmail
          }
          : {
            editorId: this.config.defaultEditorId,
            editorName: this.config.defaultEditorName,
            editorUid: this.config.defaultEditorUid,
            editorEmail: this.config.defaultEditorEmail
          }
        rows.push({
          bookingId: guessed ? guessed.id : '',
          dancerId: guessed ? guessed.dancerId : '',
          personId: guessed ? guessed.personId : null,
          uid: guessed ? guessed.uid : '',
          dancerEmail: guessed ? guessed.dancerEmail : '',
          dancerName: guessed ? guessed.dancerName : '',
          kind: 'proof',
          storagePath: path,
          downloadUrl: url,
          fileName: file.name,
          capturedAt: file.lastModified || Date.now(),
          ...editor
        })
      }
      const created = await this.$store.dispatch('addPhotoshootPhotoDocs', rows)
      this.photos = this.photos.concat(created)
      const byBooking = {}
      created.forEach((row) => {
        if (!row.bookingId) {
          return
        }
        byBooking[row.bookingId] = (byBooking[row.bookingId] || 0) + 1
      })
      const ids = Object.keys(byBooking)
      for (let i = 0; i < ids.length; i++) {
        await this.$store.dispatch('markPhotoshootBookingProofsReady', {
          bookingId: ids[i],
          addCount: byBooking[ids[i]]
        })
        const b = this.bookings.find((x) => x.id === ids[i])
        if (b) {
          b.proofsReady = true
          b.proofCount = (Number(b.proofCount) || 0) + byBooking[ids[i]]
        }
      }
      this.uploadNote = 'Uploaded ' + created.length
      setTimeout(() => { this.uploadNote = '' }, 2400)
    },
    async onInboxFiles (ev) {
      const files = ev && ev.target && ev.target.files
      await this.uploadFiles(files, null)
      if (ev && ev.target) {
        ev.target.value = ''
      }
    },
    async onBookingFiles (booking, ev) {
      const files = ev && ev.target && ev.target.files
      await this.uploadFiles(files, booking)
      if (ev && ev.target) {
        ev.target.value = ''
      }
    },
    async toggleHide (photo) {
      const hidden = !photo.hidden
      await this.$store.dispatch('updatePhotoshootPhoto', {
        id: photo.id,
        fields: {
          hidden,
          hiddenBy: hidden ? 'admin' : null
        }
      })
      photo.hidden = hidden
      photo.hiddenBy = hidden ? 'admin' : null
    },
    async moveInbox () {
      const booking = this.bookedList.find((b) => b.id === this.moveTargetId)
      const ids = this.inboxCheckedIds
      if (!booking || !ids.length) {
        return
      }
      await this.$store.dispatch('movePhotoshootPhotos', { photoIds: ids, booking })
      ids.forEach((id) => {
        const p = this.photos.find((x) => x.id === id)
        if (p) {
          p.bookingId = booking.id
          p.dancerId = booking.dancerId
          p.uid = booking.uid
          p.dancerEmail = booking.dancerEmail
          p.dancerName = booking.dancerName
        }
        this.$set(this.selectedInbox, id, false)
      })
      booking.proofsReady = true
      booking.proofCount = (Number(booking.proofCount) || 0) + ids.length
    },
    async onEditorChange (booking, ev) {
      const id = ev && ev.target ? ev.target.value : ''
      const f = this.editorFromId(id)
      const editor = f
        ? { id: f.id, name: f.name, uid: f.uid, email: f.email }
        : { id: '', name: '', uid: '', email: '' }
      const patch = await this.$store.dispatch('assignPhotoshootEditor', {
        bookingId: booking.id,
        editor
      })
      Object.assign(booking, patch)
      this.photos.forEach((p) => {
        if (p.bookingId === booking.id) {
          p.editorId = patch.editorId
          p.editorName = patch.editorName
          p.editorUid = patch.editorUid
          p.editorEmail = patch.editorEmail
        }
      })
    },
    async onEditedFile (photo, booking, ev) {
      const file = ev && ev.target && ev.target.files && ev.target.files[0]
      if (ev && ev.target) {
        ev.target.value = ''
      }
      if (!file) {
        return
      }
      const cols = this.$store.getters.adminIabf
      const prefix = (cols && cols.storage && cols.storage.photoshoot) || ('iabf-photoshoot-' + this.adminFestivalYear)
      const safe = String(file.name || 'edited.jpg').replace(/[^\w.\-]+/g, '_')
      const path = prefix + '/edited/' + booking.id + '/' + Date.now() + '-' + safe
      const snap = await storage.ref(path).put(file, { contentType: file.type || 'image/jpeg' })
      const url = await snap.ref.getDownloadURL()
      await this.$store.dispatch('updatePhotoshootPhoto', {
        id: photo.id,
        fields: {
          edited: true,
          editedUrl: url,
          editedStoragePath: path,
          editedAt: Date.now()
        }
      })
      photo.edited = true
      photo.editedUrl = url
    },
    async markReady (booking) {
      await this.$store.dispatch('markPhotoshootEditedReady', booking.id)
      booking.editedReady = true
    }
  }
}
</script>

<style scoped>
.ps-admin { max-width: 980px; }
.ps-check {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  font-size: 14px;
  font-weight: 600;
}
.page-header h2 {
  font-family: Quattrocento, Georgia, serif;
  font-weight: 400;
  font-size: 32px;
  margin: 0 0 8px;
}
.muted { color: #6b6570; font-size: 15px; }
.hint { font-size: 13px; color: #8a8792; margin: 8px 0; }
.count-note { margin-left: 8px; }
.ps-tabs { display: flex; gap: 8px; margin: 16px 0 8px; }
.ps-tab {
  border: 1px solid #d8d4ce;
  background: #fff;
  border-radius: 999px;
  padding: 6px 14px;
  font: inherit;
  cursor: pointer;
}
.ps-tab.on { background: #267E7D; color: #fff; border-color: #267E7D; }
.ps-card {
  background: #fff;
  border: 1px solid #eceae6;
  border-radius: 12px;
  padding: 16px 18px 18px;
  margin: 16px 0;
}
.ps-card h3 { margin: 0 0 12px; font-size: 18px; }
.ps-booking-head { display: flex; justify-content: space-between; gap: 12px; align-items: baseline; }
.ps-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  align-items: flex-end;
}
.ps-row label { display: flex; flex-direction: column; gap: 4px; font-size: 13px; font-weight: 600; }
.ps-input {
  font: inherit;
  font-weight: 400;
  padding: 6px 8px;
  border: 1px solid #d8d4ce;
  border-radius: 6px;
  min-width: 140px;
}
.ps-input-inline { min-width: 120px; font-weight: 400; }
.ps-day { margin-top: 16px; }
.ps-day h4 { margin: 0 0 8px; }
.ps-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.ps-table th, .ps-table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #eee; vertical-align: middle; }
.ps-table tr.is-break { background: #f7f6f4; color: #6b6570; }
.ps-table tr.is-booked { background: #eef6f1; }
.mono { font-variant-numeric: tabular-nums; white-space: nowrap; }
.ps-actions { white-space: nowrap; }
.linkish {
  border: none;
  background: none;
  color: #267E7D;
  cursor: pointer;
  font: inherit;
  font-size: 13px;
  text-decoration: underline;
  margin-right: 8px;
}
.linkish.danger { color: #b54a4a; }
.flash { margin-left: 12px; font-size: 13px; color: #2f6b4f; font-weight: 600; }
.ps-thumbs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
  margin-top: 12px;
}
.ps-thumb {
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid #eceae6;
  border-radius: 10px;
  padding: 6px;
  background: #faf9f7;
  position: relative;
}
.ps-thumb.hid { opacity: 0.4; }
.ps-thumb.picked { outline: 2px solid #267E7D; }
.ps-thumb img { width: 100%; height: 120px; object-fit: cover; border-radius: 6px; background: #ddd; }
.ps-thumb-name { font-size: 11px; color: #6b6570; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.thumb-btn {
  border: none;
  background: #fff;
  border: 1px solid #d8d4ce;
  border-radius: 6px;
  font: inherit;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
}
.queue-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 16px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  align-items: center;
}
.julie-block { margin-top: 16px; padding-top: 12px; border-top: 1px solid #eee; }
.julie-block h4 { margin: 0; }
.ps-thumb-name em { font-style: normal; color: #267E7D; }
</style>
